import React, { useState, useEffect, useRef } from 'react';
import { Bell, Clock, TrendingUp, Volume2, VolumeX } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import soundManager from '@/utils/soundManager';

// US Market Hours (Eastern Time)
const MARKET_OPEN_HOUR = 9;
const MARKET_OPEN_MINUTE = 30;
const MARKET_CLOSE_HOUR = 16;
const MARKET_CLOSE_MINUTE = 0;

const MarketTimer = () => {
  const [timeData, setTimeData] = useState({
    isMarketOpen: false,
    countdown: { hours: 0, minutes: 0, seconds: 0 },
    statusText: 'Loading...',
    nextEventTime: null
  });
  const [soundEnabled, setSoundEnabled] = useState(soundManager.isEnabled());
  const prevMarketState = useRef(null);

  // Toggle sound on/off
  const toggleSound = () => {
    const newState = !soundEnabled;
    setSoundEnabled(newState);
    soundManager.setEnabled(newState);
  };

  // Play appropriate sound based on market state change
  const playMarketSound = (isOpening) => {
    if (isOpening) {
      // Market is opening - play FAAH sound
      soundManager.playMarketOpen();
    } else {
      // Market is closing - play Undertaker Gong
      soundManager.playMarketClose();
    }
  };

  // Get current time in Eastern timezone
  const getEasternTime = () => {
    const now = new Date();
    const easternTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
    return easternTime;
  };

  // Check if it's a trading day (Mon-Fri, excluding major holidays)
  const isTradingDay = (date) => {
    const day = date.getDay();
    // 0 = Sunday, 6 = Saturday
    if (day === 0 || day === 6) return false;
    
    // Check major US market holidays (simplified)
    const month = date.getMonth();
    const dayOfMonth = date.getDate();
    
    // New Year's Day (Jan 1)
    if (month === 0 && dayOfMonth === 1) return false;
    // Christmas (Dec 25)
    if (month === 11 && dayOfMonth === 25) return false;
    // Independence Day (Jul 4)
    if (month === 6 && dayOfMonth === 4) return false;
    
    return true;
  };

  // Get next trading day
  const getNextTradingDay = (date) => {
    const nextDay = new Date(date);
    nextDay.setDate(nextDay.getDate() + 1);
    nextDay.setHours(MARKET_OPEN_HOUR, MARKET_OPEN_MINUTE, 0, 0);
    
    while (!isTradingDay(nextDay)) {
      nextDay.setDate(nextDay.getDate() + 1);
    }
    
    return nextDay;
  };

  // Calculate market status and countdown
  const calculateMarketStatus = () => {
    const now = getEasternTime();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentSecond = now.getSeconds();
    
    // Convert to minutes for easier comparison
    const currentTimeInMinutes = currentHour * 60 + currentMinute;
    const marketOpenInMinutes = MARKET_OPEN_HOUR * 60 + MARKET_OPEN_MINUTE;
    const marketCloseInMinutes = MARKET_CLOSE_HOUR * 60 + MARKET_CLOSE_MINUTE;
    
    let isMarketOpen = false;
    let targetTime;
    let statusText;
    
    if (!isTradingDay(now)) {
      // Weekend or holiday
      isMarketOpen = false;
      targetTime = getNextTradingDay(now);
      const dayName = targetTime.toLocaleDateString('en-US', { weekday: 'long' });
      statusText = `Market Closed • Opens ${dayName}`;
    } else if (currentTimeInMinutes < marketOpenInMinutes) {
      // Before market open
      isMarketOpen = false;
      targetTime = new Date(now);
      targetTime.setHours(MARKET_OPEN_HOUR, MARKET_OPEN_MINUTE, 0, 0);
      statusText = 'Pre-Market • Opens at 9:30 AM ET';
    } else if (currentTimeInMinutes >= marketOpenInMinutes && currentTimeInMinutes < marketCloseInMinutes) {
      // Market is open
      isMarketOpen = true;
      targetTime = new Date(now);
      targetTime.setHours(MARKET_CLOSE_HOUR, MARKET_CLOSE_MINUTE, 0, 0);
      statusText = 'Market Open • Closes at 4:00 PM ET';
    } else {
      // After market close
      isMarketOpen = false;
      targetTime = getNextTradingDay(now);
      const dayName = targetTime.toLocaleDateString('en-US', { weekday: 'long' });
      statusText = now.getDay() === 5 
        ? `Market Closed • Opens Monday` 
        : `After Hours • Opens Tomorrow`;
    }
    
    // Calculate countdown
    const diffMs = targetTime - now;
    const diffSeconds = Math.floor(diffMs / 1000);
    const hours = Math.floor(diffSeconds / 3600);
    const minutes = Math.floor((diffSeconds % 3600) / 60);
    const seconds = diffSeconds % 60;
    
    return {
      isMarketOpen,
      countdown: { 
        hours: Math.max(0, hours), 
        minutes: Math.max(0, minutes), 
        seconds: Math.max(0, seconds) 
      },
      statusText,
      nextEventTime: targetTime
    };
  };

  useEffect(() => {
    const updateTimer = () => {
      const newData = calculateMarketStatus();
      
      // Check for market state change (open/close sound)
      if (prevMarketState.current !== null && prevMarketState.current !== newData.isMarketOpen) {
        // Market state changed - play appropriate sound
        playMarketSound(newData.isMarketOpen);
      }
      prevMarketState.current = newData.isMarketOpen;
      
      setTimeData(newData);
    };

    // Initial update
    updateTimer();
    
    // Update every second
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, []);

  const formatTime = (num) => String(num).padStart(2, '0');

  return (
    <div className={`w-full py-1.5 sm:py-2 px-3 sm:px-4 safe-top ${
      timeData.isMarketOpen 
        ? 'bg-gradient-to-r from-green-600 via-green-500 to-emerald-500' 
        : 'bg-gradient-to-r from-slate-700 via-slate-600 to-slate-700'
    } text-white shadow-lg`}>
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-2">
        {/* Left - Status */}
        <div className="flex items-center gap-2 min-w-0">
          <div className={`p-1.5 rounded-full flex-shrink-0 ${timeData.isMarketOpen ? 'bg-white/20' : 'bg-white/10'}`}>
            {timeData.isMarketOpen ? (
              <TrendingUp className="h-4 w-4" />
            ) : (
              <Clock className="h-4 w-4" />
            )}
          </div>
          <div className="flex items-center gap-2 min-w-0">
            <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${
              timeData.isMarketOpen ? 'bg-green-300 animate-pulse' : 'bg-gray-400'
            }`}></span>
            <span className="text-xs sm:text-sm font-medium truncate">{timeData.statusText}</span>
          </div>
        </div>

        {/* Center - Countdown */}
        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          <Bell className={`hidden sm:block h-4 w-4 ${timeData.isMarketOpen ? 'text-yellow-300' : 'text-gray-400'}`} />
          <div className="flex items-center gap-1">
            <span className="text-[10px] sm:text-xs opacity-75 whitespace-nowrap">
              {timeData.isMarketOpen ? 'Closes in:' : 'Opens in:'}
            </span>
            <div className="flex items-center gap-0.5 sm:gap-1 font-mono font-bold text-sm sm:text-lg">
              <span className="bg-black/20 px-1.5 sm:px-2 py-0.5 rounded">{formatTime(timeData.countdown.hours)}</span>
              <span className="text-white/60">:</span>
              <span className="bg-black/20 px-1.5 sm:px-2 py-0.5 rounded">{formatTime(timeData.countdown.minutes)}</span>
              <span className="text-white/60">:</span>
              <span className="bg-black/20 px-1.5 sm:px-2 py-0.5 rounded">{formatTime(timeData.countdown.seconds)}</span>
            </div>
          </div>
        </div>

        {/* Right - Sound Toggle & Badge */}
        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          {/* Sound Toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleSound}
            className={`p-1.5 h-auto rounded-full hover:bg-white/20 ${
              soundEnabled ? 'text-yellow-300' : 'text-white/50'
            }`}
            title={soundEnabled ? 'Sound On (Click to mute)' : 'Sound Off (Click to unmute)'}
            data-testid="sound-toggle-button"
          >
            {soundEnabled ? (
              <Volume2 className="h-4 w-4" />
            ) : (
              <VolumeX className="h-4 w-4" />
            )}
          </Button>
          
          <Badge 
            variant="outline" 
            className={`hidden sm:inline-flex text-xs border-white/30 ${
              timeData.isMarketOpen 
                ? 'bg-white/20 text-white' 
                : 'bg-white/10 text-white/80'
            }`}
          >
            NYSE / NASDAQ
          </Badge>
          <span className="hidden sm:inline text-xs opacity-60">ET</span>
        </div>
      </div>
    </div>
  );
};

export default MarketTimer;
